// frontend/src/components/products/ProductRecommendations.js

import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Loader from "../../components/Loader";
import Message from "../../components/Message";
import ProductImage from "../../components/images/ProductImage";

import {
  listProductRecommendations,
  resetProductRecommendations,
} from "../../actions/productActions";
import { getProductDisplayPrice } from "../../utils/productPricing";

import "./ProductRecommendations.css";

const PLACEHOLDER_IMAGE = "https://placehold.co/900x1200?text=Academy+Costumes+London";

function safeText(value = "") {
  return String(value ?? "").trim();
}

function getProductUrl(product = {}) {
  const shopKey = safeText(product.shopKey);
  const identifier = safeText(product.slug || product._id || product.id);

  if (!shopKey || !identifier) return "#";

  return `/shop/${encodeURIComponent(shopKey)}/product/${encodeURIComponent(
    identifier,
  )}`;
}

function getBadge(product = {}) {
  if (product.exclusive) return "Exclusive";
  if (product.limitedEdition) return "Limited Edition";
  if (product.isEditorsPick) return "Editors’ Pick";
  if (product.isNewArrival) return "New Arrival";
  if (product.isVintage) return "Vintage";

  return "";
}

function RecommendationCard({ product }) {
  const productName = safeText(product?.name) || "Untitled product";
  const productBrand = safeText(product?.brand);
  const productUrl = getProductUrl(product);
  const price = getProductDisplayPrice(product);
  const badge = getBadge(product);

  const imageAlt = productBrand ? `${productName} by ${productBrand}` : productName;
  const canLink = productUrl !== "#";

  const image = (
    <div className="product-recommendations__image-wrap">
      <ProductImage
        product={product}
        fallback={PLACEHOLDER_IMAGE}
        alt={imageAlt}
        sizes="(max-width: 360px) 56vw, (max-width: 720px) min(47vw, 205px), (max-width: 900px) calc(50vw - 24px), (max-width: 1180px) calc(33.333vw - 24px), 300px"
        loading="lazy"
        decoding="async"
      />

      {badge ? (
        <span className="product-recommendations__badge">{badge}</span>
      ) : null}
    </div>
  );

  return (
    <article className="product-recommendations__card">
      {canLink ? (
        <Link
          to={productUrl}
          className="product-recommendations__image-link"
          aria-label={`View ${productName}`}
        >
          {image}
        </Link>
      ) : (
        <div className="product-recommendations__image-link">{image}</div>
      )}

      <div className="product-recommendations__copy">
        <h3>
          {canLink ? (
            <Link to={productUrl}>{productName}</Link>
          ) : (
            <span>{productName}</span>
          )}
        </h3>

        {price ? <p>{price}</p> : null}
      </div>
    </article>
  );
}

export default function ProductRecommendations({
  productId,
  title = "You might also like",
  eyebrow = "Selected for you",
  limit = 8,
  className = "",
  showWhenEmpty = false,
}) {
  const dispatch = useDispatch();

  const {
    loading = false,
    products = [],
    error = null,
    sourceProductId = "",
  } = useSelector((state) => state.productRecommendations || {});

  const safeProductId = safeText(productId);

  useEffect(() => {
    if (!safeProductId) return undefined;

    dispatch(listProductRecommendations(safeProductId, { limit })).catch(
      () => undefined,
    );

    return () => {
      dispatch(resetProductRecommendations());
    };
  }, [dispatch, safeProductId, limit]);

  const visibleProducts = useMemo(() => {
    const list = Array.isArray(products) ? products : [];

    return list.filter((product) => {
      const id = safeText(product?._id || product?.id);
      return id && id !== safeProductId;
    });
  }, [products, safeProductId]);

  const shouldShowEmpty =
    !loading && !error && !visibleProducts.length && showWhenEmpty;

  if (!safeProductId) return null;

  if (!loading && !error && !visibleProducts.length && !shouldShowEmpty) {
    return null;
  }

  return (
    <section
      className={["product-recommendations", className]
        .filter(Boolean)
        .join(" ")}
      aria-label={title}
      data-source-product-id={sourceProductId || safeProductId}
    >
      <div className="product-recommendations__head">
        {eyebrow ? <p>{eyebrow}</p> : null}
        <h2>{title}</h2>
      </div>

      {loading ? (
        <div className="product-recommendations__state">
          <Loader />
        </div>
      ) : null}

      {!loading && error ? (
        <div className="product-recommendations__state">
          <Message variant="danger">{error}</Message>
        </div>
      ) : null}

      {shouldShowEmpty ? (
        <div className="product-recommendations__empty">
          <p>More recommendations will appear here soon.</p>
        </div>
      ) : null}

      {!loading && !error && visibleProducts.length ? (
        <div className="product-recommendations__grid">
          {visibleProducts.map((product) => (
            <RecommendationCard
              key={product._id || product.id || product.slug}
              product={product}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
